import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PenTool, Save, Loader2, FileDown, Share } from 'lucide-react';
import TermsSignaturePad from './TermsSignaturePad';
import MobileSignatureModal from './MobileSignatureModal';
import MedicalRecordPDFView from './MedicalRecordPDFView';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePDF } from 'react-to-pdf';
import { useToast } from '@/hooks/use-toast';
interface MedicalRecord {
  id?: string;
  hasPreviewHeartCondition?: boolean;
  isPregnant?: boolean;
  cardiovascularConditions: string[];
  metabolicConditions: string[];
  respiratoryConditions: string[];
  musculoskeletalConditions: string[];
  otherConditions: string[];
  thyroidConditions: string[];
  symptomsAndConditions: Record<string, boolean>;
  physicalActivities: string[];
  additionalNotes?: string;
  signatureData?: string;
  signatureDate?: string;
}
interface ClientMedicalRecordFormProps {
  clientId: string;
  clientName: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    birthDate?: string;
    weightKg?: number;
    heightCm?: number;
  };
  medicalRecord?: MedicalRecord | null;
  onSave: (record: MedicalRecord) => Promise<void>;
  onTermsAcceptance?: (signatureData: string) => Promise<void>;
  isSaving?: boolean;
  isRecording?: boolean;
}
const parseBool = (value: string): boolean | undefined => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return undefined;
};
const ClientMedicalRecordForm: React.FC<ClientMedicalRecordFormProps> = ({
  clientId,
  clientName,
  client,
  medicalRecord,
  onSave,
  onTermsAcceptance,
  isSaving = false,
  isRecording = false
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [mobileSignatureModalOpen, setMobileSignatureModalOpen] = useState(false);

  // PDF generation setup
  const { toPDF, targetRef } = usePDF({
    filename: `medical-record-${clientName.replace(/\s+/g, '-').toLowerCase()}.pdf`,
    page: {
      margin: 20,
      format: 'A4',
    }
  });
  const [formData, setFormData] = useState<MedicalRecord>({
    hasPreviewHeartCondition: undefined,
    isPregnant: undefined,
    cardiovascularConditions: [],
    metabolicConditions: [],
    respiratoryConditions: [],
    musculoskeletalConditions: [],
    otherConditions: [],
    thyroidConditions: [],
    symptomsAndConditions: {},
    physicalActivities: [],
    additionalNotes: '',
    signatureData: undefined,
    signatureDate: undefined,
    ...medicalRecord
  });
  const [currentSignature, setCurrentSignature] = useState<string | null>(formData.signatureData || null);

  // Update formData when medicalRecord prop changes
  useEffect(() => {
    if (medicalRecord) {
      setFormData(prev => ({
        ...prev,
        ...medicalRecord
      }));
      // Reset dirty state when new data is loaded
      setIsDirty(false);
    }
  }, [medicalRecord]);

  const cardiovascularOptions = t('medicalRecord.cardiovascularConditions.options', { returnObjects: true }) as string[];
  const metabolicOptions = t('medicalRecord.metabolicConditions.options', { returnObjects: true }) as string[];
  const respiratoryOptions = t('medicalRecord.respiratoryConditions.options', { returnObjects: true }) as string[];
  const musculoskeletalOptions = t('medicalRecord.musculoskeletalConditions.options', { returnObjects: true }) as string[];
  const otherConditionsOptions = t('medicalRecord.otherConditions.options', { returnObjects: true }) as string[];
  const thyroidOptions = t('medicalRecord.thyroidConditions.options', { returnObjects: true }) as string[];
  const physicalActivitiesOptions = t('medicalRecord.physicalActivities.options', { returnObjects: true }) as string[];
  const symptomsQuestions = t('medicalRecord.symptomsAndConditions.questions', { returnObjects: true }) as Array<{
    key: string;
    label: string;
  }>;
  const handleConditionChange = (category: keyof Pick<MedicalRecord, 'cardiovascularConditions' | 'metabolicConditions' | 'respiratoryConditions' | 'musculoskeletalConditions' | 'otherConditions' | 'thyroidConditions' | 'physicalActivities'>, condition: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [category]: checked ? [...prev[category], condition] : prev[category].filter(c => c !== condition)
    }));
    setIsDirty(true);
  };
  const handleSymptomChange = (symptom: string, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      symptomsAndConditions: {
        ...prev.symptomsAndConditions,
        [symptom]: value
      }
    }));
    setIsDirty(true);
  };
  const handleSave = async () => {
    const recordToSave: MedicalRecord = {
      ...formData,
      signatureData: formData.signatureData,
      signatureDate: formData.signatureData ? (formData.signatureDate || new Date().toISOString()) : undefined
    };
    
    console.log('💾 Saving medical record with signature:', {
      hasSignature: !!recordToSave.signatureData,
      signatureLength: recordToSave.signatureData?.length
    });
    
    await onSave(recordToSave);

    // If there's a signature and terms acceptance handler, record terms acceptance
    if (recordToSave.signatureData && onTermsAcceptance) {
      console.log('📝 Recording terms acceptance with signature');
      await onTermsAcceptance(recordToSave.signatureData);
    }
    setIsDirty(false);
  };

  const handleExportToPDF = async () => {
    try {
      toPDF();
      toast({
        title: t('medicalRecord.messages.success'),
        description: t('medicalRecord.messages.pdfExportSuccess'),
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: t('medicalRecord.messages.error'),
        description: t('medicalRecord.messages.pdfExportError'),
        variant: "destructive",
      });
    }
  };
  const handleSignatureChange = (signatureData: string | null) => {
    console.log('🖊️ Medical form signature changed:', signatureData ? 'has data' : 'null');
    setFormData(prev => ({
      ...prev,
      signatureData: signatureData || undefined,
      signatureDate: signatureData ? new Date().toISOString() : undefined
    }));
    setCurrentSignature(signatureData);
    if (signatureData) {
      setIsDirty(true);
    }
  };


  const handleSharePDF = async () => {
    try {
      // Check if Web Share API is available before generating PDF
      const canShareFiles = navigator.canShare && navigator.canShare({ files: [] });
      const canShareText = !!navigator.share;
      
      console.log('📱 Share capabilities:', { canShareFiles, canShareText });

      // Use html2canvas and jsPDF to generate a blob instead of using toPDF directly
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      if (!targetRef.current) {
        throw new Error('PDF target element not found');
      }

      // Generate canvas from the hidden PDF view
      const canvas = await html2canvas(targetRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: targetRef.current.scrollWidth,
        height: targetRef.current.scrollHeight
      });

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generate blob
      const pdfBlob = pdf.output('blob');
      const fileName = `${clientName} - ${t('medicalRecord.messages.pdfFileName')}.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      console.log('✅ PDF generated successfully for sharing');
      console.log('📄 PDF file created:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      const subject = `${t('medicalRecord.messages.shareSubject')} - ${clientName}`;
      const body = t('medicalRecord.messages.shareBody', { clientName });

      // Try different sharing methods in order of preference
      let shareSuccessful = false;

      // Method 1: Native file sharing (mobile primarily)
      if (canShareFiles) {
        try {
          const shareData = {
            files: [file],
            title: subject,
            text: body,
          };
          
          // Check if we can share this specific data
          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            shareSuccessful = true;
          }
        } catch (shareError) {
          console.warn('File sharing failed, trying alternative methods:', shareError);
        }
      }

      // Method 2: Text-only sharing (mobile/desktop)
      if (!shareSuccessful && canShareText) {
        try {
          await navigator.share({
            title: subject,
            text: body,
            url: `data:application/pdf;base64,${btoa(String.fromCharCode(...new Uint8Array(await pdfBlob.arrayBuffer())))}`,
          });
          shareSuccessful = true;
        } catch (shareError) {
          console.warn('Text sharing failed, trying fallback methods:', shareError);
        }
      }

      // Method 3: Email fallback
      if (!shareSuccessful && client?.email) {
        try {
          const mailtoLink = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
          window.open(mailtoLink, '_blank');
          shareSuccessful = true;
        } catch (emailError) {
          console.warn('Email sharing failed:', emailError);
        }
      }

      // Method 4: Download fallback (always available)
      if (!shareSuccessful) {
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      toast({
        title: t('medicalRecord.messages.success'),
        description: shareSuccessful 
          ? t('medicalRecord.messages.pdfShareSuccess')
          : t('medicalRecord.messages.pdfExportSuccess'),
      });
    } catch (error) {
      console.error('Error sharing PDF:', error);
      toast({
        title: t('medicalRecord.messages.error'),
        description: t('medicalRecord.messages.pdfShareError'),
        variant: "destructive",
      });
    }
  };

  // Check if medical record is saved (has an ID)
  const isMedicalRecordSaved = medicalRecord?.id;
  return <>
    {/* Hidden PDF View for export */}
    <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
      <div ref={targetRef}>
        {client && (
          <MedicalRecordPDFView
            client={client}
            medicalRecord={{ ...formData, signatureData: formData.signatureData }}
          />
        )}
      </div>
    </div>

    {/* Main Form */}
    <div className="space-y-6">
      <ScrollArea className="h-[70vh] pr-4">
        <div className="space-y-6">
          {/* Basic Questions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('medicalRecord.basicQuestions.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label>{t('medicalRecord.basicQuestions.heartCondition')}</Label>
                <RadioGroup value={formData.hasPreviewHeartCondition?.toString() || ''} onValueChange={value => {
                  setFormData(prev => ({
                    ...prev,
                    hasPreviewHeartCondition: parseBool(value)
                  }));
                  setIsDirty(true);
                }}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="heart-yes" />
                    <Label htmlFor="heart-yes">{t('medicalRecord.basicQuestions.yes')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="heart-no" />
                    <Label htmlFor="heart-no">{t('medicalRecord.basicQuestions.no')}</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label>{t('medicalRecord.basicQuestions.pregnant')}</Label>
                <RadioGroup value={formData.isPregnant?.toString() || ''} onValueChange={value => {
                  setFormData(prev => ({
                    ...prev,
                    isPregnant: parseBool(value)
                  }));
                  setIsDirty(true);
                }}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="pregnant-yes" />
                    <Label htmlFor="pregnant-yes">{t('medicalRecord.basicQuestions.yes')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="pregnant-no" />
                    <Label htmlFor="pregnant-no">{t('medicalRecord.basicQuestions.no')}</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Cardiovascular Conditions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('medicalRecord.cardiovascularConditions.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {cardiovascularOptions.map(condition => <div key={condition} className="flex items-center space-x-2">
                  <Checkbox id={`cardio-${condition}`} checked={formData.cardiovascularConditions.includes(condition)} onCheckedChange={checked => handleConditionChange('cardiovascularConditions', condition, checked as boolean)} />
                  <Label htmlFor={`cardio-${condition}`} className="text-sm">{condition}</Label>
                </div>)}
              </div>
            </CardContent>
          </Card>

          {/* Metabolic Conditions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('medicalRecord.metabolicConditions.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {metabolicOptions.map(condition => <div key={condition} className="flex items-center space-x-2">
                  <Checkbox id={`metabolic-${condition}`} checked={formData.metabolicConditions.includes(condition)} onCheckedChange={checked => handleConditionChange('metabolicConditions', condition, checked as boolean)} />
                  <Label htmlFor={`metabolic-${condition}`} className="text-sm">{condition}</Label>
                </div>)}
              </div>
            </CardContent>
          </Card>

          {/* Respiratory Conditions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('medicalRecord.respiratoryConditions.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {respiratoryOptions.map(condition => <div key={condition} className="flex items-center space-x-2">
                  <Checkbox id={`respiratory-${condition}`} checked={formData.respiratoryConditions.includes(condition)} onCheckedChange={checked => handleConditionChange('respiratoryConditions', condition, checked as boolean)} />
                  <Label htmlFor={`respiratory-${condition}`} className="text-sm">{condition}</Label>
                </div>)}
              </div>
            </CardContent>
          </Card>

          {/* Musculoskeletal Conditions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('medicalRecord.musculoskeletalConditions.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {musculoskeletalOptions.map(condition => <div key={condition} className="flex items-center space-x-2">
                  <Checkbox id={`musculo-${condition}`} checked={formData.musculoskeletalConditions.includes(condition)} onCheckedChange={checked => handleConditionChange('musculoskeletalConditions', condition, checked as boolean)} />
                  <Label htmlFor={`musculo-${condition}`} className="text-sm">{condition}</Label>
                </div>)}
              </div>
            </CardContent>
          </Card>

          {/* Other Conditions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('medicalRecord.otherConditions.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {otherConditionsOptions.map(condition => <div key={condition} className="flex items-center space-x-2">
                  <Checkbox id={`other-${condition}`} checked={formData.otherConditions.includes(condition)} onCheckedChange={checked => handleConditionChange('otherConditions', condition, checked as boolean)} />
                  <Label htmlFor={`other-${condition}`} className="text-sm">{condition}</Label>
                </div>)}
              </div>
            </CardContent>
          </Card>

          {/* Thyroid Conditions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('medicalRecord.thyroidConditions.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {thyroidOptions.map(condition => <div key={condition} className="flex items-center space-x-2">
                  <Checkbox id={`thyroid-${condition}`} checked={formData.thyroidConditions.includes(condition)} onCheckedChange={checked => handleConditionChange('thyroidConditions', condition, checked as boolean)} />
                  <Label htmlFor={`thyroid-${condition}`} className="text-sm">{condition}</Label>
                </div>)}
              </div>
            </CardContent>
          </Card>

          {/* Symptoms Questions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('medicalRecord.symptomsAndConditions.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {symptomsQuestions.map((question, index) => <div key={question.key} className="space-y-3">
                <Label className="text-sm">{index + 1}. {question.label}</Label>
                <RadioGroup value={formData.symptomsAndConditions[question.key]?.toString() || ''} onValueChange={value => handleSymptomChange(question.key, value === 'true')}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id={`${question.key}-yes`} />
                    <Label htmlFor={`${question.key}-yes`}>{t('medicalRecord.basicQuestions.yes')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id={`${question.key}-no`} />
                    <Label htmlFor={`${question.key}-no`}>{t('medicalRecord.basicQuestions.no')}</Label>
                  </div>
                </RadioGroup>
              </div>)}
            </CardContent>
          </Card>

          {/* Physical Activities */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('medicalRecord.physicalActivities.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {physicalActivitiesOptions.map(activity => <div key={activity} className="flex items-center space-x-2">
                  <Checkbox id={`activity-${activity}`} checked={formData.physicalActivities.includes(activity)} onCheckedChange={checked => handleConditionChange('physicalActivities', activity, checked as boolean)} />
                  <Label htmlFor={`activity-${activity}`} className="text-sm">{activity}</Label>
                </div>)}
              </div>
            </CardContent>
          </Card>

          {/* Additional Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('medicalRecord.additionalNotes.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea placeholder={t('medicalRecord.additionalNotes.placeholder')} value={formData.additionalNotes || ''} onChange={e => {
                setFormData(prev => ({
                  ...prev,
                  additionalNotes: e.target.value
                }));
                setIsDirty(true);
              }} rows={4} />
            </CardContent>
          </Card>

          {/* Signature Section */}
          <Card>
            <CardContent className="space-y-4 px-[16px] py-[8px]">
              {isMobile ? <div className="space-y-4">
                <Button onClick={() => setMobileSignatureModalOpen(true)} className="w-full" variant={formData.signatureData ? "default" : "outline"}>
                  <PenTool className="h-4 w-4 mr-2" />
                  {formData.signatureData ? t('medicalRecord.signature.edit') : t('medicalRecord.signature.add')}
                </Button>

                {formData.signatureData && <Alert>
                  <AlertDescription className="text-sm">
                    {t('medicalRecord.signature.successMessage')}
                  </AlertDescription>
                </Alert>}
              </div> : <TermsSignaturePad onSignatureChange={handleSignatureChange} disabled={isSaving || isRecording} existingSignature={currentSignature ?? formData.signatureData ?? null} fullScreen={false} hideLabel={false} 
              />}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            {/* Save Button */}
            <Button onClick={handleSave} disabled={isSaving || isRecording} className="w-full sm:w-auto">
              {isSaving || isRecording ? <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isRecording ? t('medicalRecord.actions.savingWithTerms') : t('medicalRecord.actions.saving')}
              </> : <>
                <Save className="h-4 w-4 mr-2" />
                {t('medicalRecord.actions.saveRecord')}
              </>}
            </Button>

            {/* PDF Export and Share Buttons - disable until saved and no unsaved changes */}
            <div className="flex gap-2 items-center justify-center">
              <Button onClick={handleExportToPDF} variant="outline" className="flex items-center gap-2" disabled={!isMedicalRecordSaved || isDirty || isSaving || isRecording}>
                <FileDown className="h-4 w-4" />{t('medicalRecord.actions.exportPdf')}
              </Button>
              <Button onClick={handleSharePDF} variant="outline" className="flex items-center gap-2" disabled={!isMedicalRecordSaved || isDirty || isSaving || isRecording}>
                <Share className="h-4 w-4" />{t('medicalRecord.actions.sharePdf')}
              </Button>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Mobile Signature Modal */}
      {isMobile && <MobileSignatureModal open={mobileSignatureModalOpen} onOpenChange={setMobileSignatureModalOpen} clientName={clientName} signatureData={formData.signatureData} onSignatureChange={handleSignatureChange} existingSignature={currentSignature ?? formData.signatureData ?? null} disabled={isSaving} onConfirm={() => {
        // Just close the modal, signature is already set
      }} isProcessing={false} 
      />}
    </div>
  </>;
};
export default ClientMedicalRecordForm;